// =========================================================
// Vojjin Slider Utilities
// =========================================================
import {registerGlobal} from "./core.js";

/* global Logger */
class Slider {

    static sliders = [];

    /**
     * Initializes and returns a new slider instance for the specified selector.
     *
     * This function creates a new instance of RockersSlider for the provided
     * selector and configuration options. If the selector does not resolve to
     * a valid DOM element, an error is logged, and null is returned.
     *
     * @param {string} selector - A CSS selector string used to find the wrapper element for the slider.
     * @param {Object} [options={}] - Optional configuration options for initializing the slider instance.
     * @returns {RockersSlider|null} - Returns the initialized slider instance, or null if the wrapper element is not valid.
     */
    static slide = (selector, options = {}) => {
        let wrapper = $$(selector);
        if (!wrapper.defined()) {
            Logger.error('AutoSlider: invalid wrapper element');
            return null;
        }
        const instance = new RockersSlider(wrapper.element, options);
        this.sliders.push(instance);
        return instance;
    }

    /**
     * Destroys all sliders in the current context and clears the sliders list.
     *
     * This method iterates over all slider instances present in the `sliders` array,
     * invoking their `destroy` method to safely release any resources and perform
     * necessary cleanup. Once all sliders are destroyed, the list of sliders is
     * reset to an empty array.
     */
    static destroyAll = () => {
        this.sliders.forEach(slider => slider.destroy());
        this.sliders = [];
    }
}

class RockersSlider {

    constructor(wrapper, options = {}) {
        this.wrapper = wrapper;
        this.slideSelector = options.slideSelector ?? null;
        this.slides = this._collectSlides();
        this.visible = options.visible ?? 1;
        this.gap = options.gap ?? 16; // px
        this.slideWidth = 0;

        this.current = 0;
        this.running = true;

        this.displayTime = options.displayTime ?? 3000;
        this.transitionTime = options.transitionTime ?? 1000;
        this.direction = options.direction ?? 'left';
        this.pauseOnHover = options.pauseOnHover ?? true;

        this._setup();
        this._start();
        if (this.pauseOnHover) this._bindHover();
        window.addEventListener('resize', () => this._setup());

    }

    _collectSlides() {
        if (this.slideSelector)
            return Array.from(this.wrapper.querySelectorAll(this.slideSelector));
        return Array.from(this.wrapper.children);
    }

    _setup() {
        this.width = this.wrapper.clientWidth;
        this.total = this.slides.length;
        const gapTotal = (this.visible - 1) * this.gap;
        this.slideWidth = (this.width - gapTotal) / this.visible;

        if (!this.track) {
            this.track = document.createElement("div");
            this.track.classList.add("slider-track");

            // Move slides into track
            while (this.wrapper.firstChild) {
                this.track.appendChild(this.wrapper.firstChild);
            }
            this.wrapper.appendChild(this.track);
        }

        Object.assign(this.wrapper.style, {
            position: "relative",
            overflow: "hidden",
            width: `${this.width}px`,
            display: "block"
        });

        Object.assign(this.track.style, {
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: `${this.gap}px`,
            transition: `transform ${this.transitionTime}ms ease-in-out`,
            width: `${(this.slideWidth + this.gap) * this.total - this.gap}px`,
            willChange: "transform"
        });

        this.slides.forEach((slide) => {
            Object.assign(slide.style, {
                flex: `0 0 ${this.slideWidth}px`,
                width: `${this.slideWidth}px`,
                height: "100%",
            });
        });

        this.current = 0;
        this.track.style.transform = `translateX(0px)`;
    }

    _goNext() {
        if (!this.running) return;

        const stepSize = this.slideWidth + this.gap;

        if (this.direction === "left") {
            // Animate normally
            this.track.style.transition = `transform ${this.transitionTime}ms ease-in-out`;
            this.track.style.transform = `translateX(-${stepSize}px)`;

            // After transition completes:
            setTimeout(() => {
                this.track.style.transition = "none"; // disable transition
                this._moveSlideToEnd(); // move first slide to end
                this.track.style.transform = "translateX(0px)"; // reset position instantly
            }, this.transitionTime);
        } else {
            // Sliding right
            this.track.style.transition = "none";
            this._moveSlideToStart();
            this.track.style.transform = `translateX(-${stepSize}px)`;

            // Animate to position 0
            requestAnimationFrame(() => {
                this.track.style.transition = `transform ${this.transitionTime}ms ease-in-out`;
                this.track.style.transform = "translateX(0px)";
            });
        }
    }

    _moveSlideToEnd() {
        const firstSlide = this.slides[0];
        this.track.appendChild(firstSlide); // move to end
        this.slides.push(this.slides.shift()); // update order in array
    }

    _moveSlideToStart() {
        const lastSlide = this.slides[this.slides.length - 1];
        this.track.insertBefore(lastSlide, this.slides[0]); // move to start
        this.slides.unshift(this.slides.pop()); // update order in array
    }

    _start() {
        this.timer = setInterval(() => this._goNext(), this.displayTime + this.transitionTime);
    }

    _bindHover() {
        this.wrapper.addEventListener('mouseenter', () => this.pause());
        this.wrapper.addEventListener('mouseleave', () => this.resume());
    }

    pause() {
        this.running = false;
        clearInterval(this.timer);
    }

    resume() {
        this.running = true;
        this._start();
    }

    destroy() {
        this.pause();
        this.slides.forEach(slide => slide.removeAttribute('style'));
    }

}

registerGlobal('Slider', Slider);

